import { Gem, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState, FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import { StonePriceRuleStoneType, type StonePriceRule } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { BASE_CURRENCY } from '@/shared/catalog/base-currency';
import { session } from '@/shared/session/session';

import { DictionaryDeleteDialog } from '../components/dictionary-delete-dialog';
import { StonePriceRuleDialog } from '../components/stone-price-rule-dialog';
import { useDeleteStonePriceRule } from '../hooks/use-stone-price-rule-mutations';
import { useStonePriceRules } from '../hooks/use-stone-price-rules';

type Editing = { kind: 'new' } | { kind: 'edit'; rule: StonePriceRule } | null;

/** Bands in reading order: per stone type, store-wide first, then by basis and lower bound. */
const byBand = (a: StonePriceRule, b: StonePriceRule) =>
  (a.storeId ?? '').localeCompare(b.storeId ?? '') ||
  a.basis.localeCompare(b.basis) ||
  a.rangeFrom - b.rangeFrom;

function StonePricing() {
  const { t } = useTranslation(['dictionary', 'products']);
  const format = useFormat();
  const { me } = session.useSession();
  const stores = me?.stores ?? [];
  const query = useStonePriceRules();
  const remove = useDeleteStonePriceRule();
  const [editing, setEditing] = useState<Editing>(null);
  const [deleting, setDeleting] = useState<StonePriceRule | null>(null);
  const rules = query.data;
  const tourStoneType = Object.values(StonePriceRuleStoneType).find((type) =>
    rules?.some((r) => r.stoneType === type),
  );

  const storeName = (storeId: string | null) =>
    storeId
      ? (stores.find((s) => s.id === storeId)?.name ?? storeId)
      : t('dictionary:stonePricing.allStores');
  const range = (rule: StonePriceRule) =>
    rule.basis === 'carat'
      ? t('dictionary:stonePricing.rangeCarat', { from: rule.rangeFrom, to: rule.rangeTo })
      : t('dictionary:stonePricing.rangeCost', {
          from: format.money({ amount: rule.rangeFrom, currency: BASE_CURRENCY }),
          to: format.money({ amount: rule.rangeTo, currency: BASE_CURRENCY }),
        });

  const newButton = (tour?: string) => (
    <Can permission="configurator.write">
      <Button onClick={() => setEditing({ kind: 'new' })} data-tour={tour}>
        <Plus aria-hidden />
        {t('dictionary:stonePricing.new')}
      </Button>
    </Can>
  );

  return (
    <PageContent>
      <PageHeader
        title={t('dictionary:stonePricing.title')}
        description={t('dictionary:stonePricing.description')}
        actions={newButton('dictionary.stone-pricing.new-button')}
      />
      {query.error ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : !rules ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-12" />
          ))}
        </div>
      ) : rules.length === 0 ? (
        <EmptyState
          icon={Gem}
          title={t('dictionary:stonePricing.empty.title')}
          description={t('dictionary:stonePricing.empty.description')}
          action={newButton()}
          tour="dictionary.stone-pricing.empty"
          tourLink
        />
      ) : (
        <div className="flex flex-col gap-4">
          {Object.values(StonePriceRuleStoneType).map((stoneType) => {
            const bands = rules.filter((r) => r.stoneType === stoneType).sort(byBand);
            const label = t(`products:configurator.stoneType.${stoneType}`);
            return (
              <Card
                key={stoneType}
                className="gap-0 py-0"
                data-tour={
                  stoneType === tourStoneType
                    ? 'dictionary.stone-pricing.stone-type-card'
                    : undefined
                }
              >
                <CardHeader className="border-b px-6 py-4">
                  <CardTitle className="text-[15px]">{label}</CardTitle>
                </CardHeader>
                <CardContent className="px-0">
                  {bands.length === 0 ? (
                    <p className="text-muted-foreground px-6 py-4 text-sm">
                      {t('dictionary:stonePricing.noBands', { type: label })}
                    </p>
                  ) : (
                    <Table aria-label={label}>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="pl-6">
                            {t('dictionary:stonePricing.columns.range')}
                          </TableHead>
                          <TableHead>{t('dictionary:stonePricing.columns.margin')}</TableHead>
                          <TableHead
                            data-tour={
                              stoneType === tourStoneType
                                ? 'dictionary.stone-pricing.store-column'
                                : undefined
                            }
                          >
                            {t('dictionary:stonePricing.columns.store')}
                          </TableHead>
                          <TableHead className="pr-6 text-right">
                            {t('dictionary:columns.actions')}
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bands.map((rule) => (
                          <TableRow key={rule.id} aria-label={range(rule)}>
                            <TableCell className="pl-6 tabular-nums">
                              <div className="flex items-center gap-2">
                                {range(rule)}
                                <Badge variant="outline" className="font-normal">
                                  {t(`dictionary:stonePricing.basis.${rule.basis}`)}
                                </Badge>
                              </div>
                            </TableCell>
                            <TableCell className="tabular-nums">
                              {t('dictionary:stonePricing.marginValue', {
                                pct: rule.marginPct,
                                factor: format.number(1 + rule.marginPct / 100),
                              })}
                            </TableCell>
                            <TableCell className="text-sm">{storeName(rule.storeId)}</TableCell>
                            <TableCell className="pr-6 text-right">
                              <Can permission="configurator.write">
                                <div className="flex justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    aria-label={t('dictionary:stonePricing.editFor', {
                                      range: range(rule),
                                    })}
                                    onClick={() => setEditing({ kind: 'edit', rule })}
                                  >
                                    <Pencil aria-hidden />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    className="text-destructive hover:text-destructive"
                                    aria-label={t('dictionary:stonePricing.deleteFor', {
                                      range: range(rule),
                                    })}
                                    onClick={() => setDeleting(rule)}
                                  >
                                    <Trash2 aria-hidden />
                                  </Button>
                                </div>
                              </Can>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
      <StonePriceRuleDialog
        rule={editing?.kind === 'edit' ? editing.rule : null}
        stores={stores}
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
      />
      <DictionaryDeleteDialog
        open={deleting !== null}
        title={t('dictionary:stonePricing.delete.title')}
        description={t('dictionary:stonePricing.delete.description')}
        inUseMessage={t('dictionary:stonePricing.delete.description')}
        pending={remove.isPending}
        error={remove.error}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting.id, { onSuccess: () => setDeleting(null) });
        }}
        onOpenChange={(open) => {
          if (!open) {
            remove.reset();
            setDeleting(null);
          }
        }}
      />
    </PageContent>
  );
}

export function StonePricingPage() {
  return (
    <FeatureErrorBoundary name="dictionary.stonePricing">
      <StonePricing />
    </FeatureErrorBoundary>
  );
}
